import { ReactNode, useEffect, useRef, useState, useCallback } from "react";
import {
  Button,
  Form,
  Input,
  InputGroup,
  InputNumber,
  SelectPicker,
  Stack,
} from "rsuite";
import { TOffer, TPartner } from "types/offers-temporary";
import BlockIcon from "@rsuite/icons/Block";
import FileUpload from "@rsuite/icons/FileUpload";
import Reload from "@rsuite/icons/Reload";
import CloseIcon from "@rsuite/icons/Close";
import countryList from "country-list";
import { countryNames, getCountryFlag } from "../../../constants/countries";
import "./_offers-crud.scss";
import { PanelService } from "network/panel/network";
import { createLogger } from '../../../utils/logger';
import {
  Events,
  FileUploadWithPreview,
  ImageAddedEvent,
} from "file-upload-with-preview";
import "file-upload-with-preview/dist/style.css";

import {
  loadFileIntoImgSrc,
  loadFileIntoImgsSrc,
  loadPathIntoImgSrc,
  loadPathIntoImgsSrc,
} from "utils/general";
import type { TOfferwallType } from "types/offerwall-type";
import { OFFER_IMAGE_BASE_URL as OFFER_IMAGE_API_BASE_URL } from "config/runtime";
const logger = createLogger('offer-crud');
const bgUpload = new FileUploadWithPreview("offer-bg-upload");
const fgUpload = new FileUploadWithPreview("offer-fg-upload");

interface ItemDataType<T = number | string> extends Record<string, any> {
  label?: string | React.ReactNode;
  value?: T;
  groupBy?: string;
  parent?: ItemDataType<T>;
  children?: ItemDataType<T>[];
  loading?: boolean;
}

interface IOfferCrudParams {
  offer?: TOffer;
  offers: TOffer[];
  partners: TPartner[];
  onClose: () => void;
  onSave?: () => void;
}

export const OfferCrudModal = (params: IOfferCrudParams) => {
  let { offers, partners, onClose, onSave } = params;

  let [_, setTime] = useState(Date.now());
  let [isClosing, setIsClosing] = useState(false);

  let update = () => {
    setTime(Date.now());
  };

  let [hasChanged, setHasChanged] = useState(false);
  let [isSaving, setIsSaving] = useState(false);
  let [isDeleting, setIsDeleting] = useState(false);
  let [confirmDelete, setConfirmDelete] = useState(false);
  let [saveError, setSaveError] = useState<string | null>(null);
  let [offer, setOffer] = useState<Partial<TOffer>>(params.offer || {});

  // Use local storage for images - graphic fields contain paths like "data/offer-images/uuid.png"
  const getImageUrl = (path: string | undefined): string => {
    if (!path) return '';
    if (path.includes('data/offer-images/')) {
      const filename = path.split('/').pop();
      return `${OFFER_IMAGE_API_BASE_URL}/offers/images/${filename}`;
    }
    return path;
  };
  
  let bg = offer.graphic?.bg ? getImageUrl(offer.graphic.bg) : '';
  let fg = offer.graphic?.logo ? getImageUrl(offer.graphic.logo) : '';

  let [countryListMapped] = useState(
    countryList
      .getData()
      .map((data) => ({ 
        label: (
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '18px' }}>{getCountryFlag(data.code)}</span>
            <span style={{ flex: 1 }}>{data.name}</span>
            <span style={{ fontWeight: 700, color: '#888' }}>{data.code}</span>
          </span>
        ), 
        value: data.code,
        searchLabel: `${data.name} ${data.code}`
      }))
  );

  let [partnersMapped] = useState(
    partners.map((item, index) => ({ label: item.name, value: index }))
  );

  let [id, setId] = useState(params.offer?.id || null);
  let [partnerIndex, setPartnerIndex] = useState(
    params.offer
      ? partners.findIndex((p) => p.partnerId == offer?.partnerId)
      : null
  );
  let [geo, setGeo] = useState(params.offer?.geo || null);
  let [link, setLink] = useState(params.offer?.link || null);
  let [idUnlocked, setIdUnlocked] = useState(false);
  let [nameUnlocked, setNameUnlocked] = useState(false);
  let [fgRemoved, setFgRemoved] = useState(false);
  let [bgRemoved, setBgRemoved] = useState(false);
  const offerwallType: TOfferwallType = 'MFO';
  let [texts, setTexts] = useState<string[]>((params.offer as any)?.texts || ['', '', '', '', '', '']);
  let [noBackground, setNoBackground] = useState<boolean>((params.offer as any)?.noBackground || false);
  let [generatedKey, setGeneratedKey] = useState<string>((params.offer as any)?.generatedKey || '');
  const isMfoLike = true;

  useEffect(() => {
    setGeneratedKey((params.offer as any)?.generatedKey || '');
  }, [params.offer?.id]);

  // Auto-populate geo badge text only when user changes geo (not on initial load)
  const isInitialGeo = useRef(true);
  useEffect(() => {
    if (isInitialGeo.current) {
      isInitialGeo.current = false;
      return;
    }
    if (geo && isMfoLike) {
      setTexts(prev => {
        if (!prev[4]) {
          const newTexts = [...prev];
          if ((geo as string).toUpperCase() === 'UA') {
            newTexts[4] = '';
            return newTexts;
          }
          const flag = getCountryFlag(geo as string);
          newTexts[4] = `${flag} ${geo}`;
          return newTexts;
        }
        return prev;
      });
    }
  }, [geo, isMfoLike]);

  // Reload images when offerwall type changes (DOM elements change)
  useEffect(() => {
    // Small delay to ensure DOM has updated
    setTimeout(() => {
      updateOfferImages();
    }, 50);
  }, [offerwallType]);

  let updateOfferImages = (fgWasRemoved = fgRemoved, bgWasRemoved = bgRemoved) => {
    if (fgUpload.cachedFileArray.length > 0) {
      loadFileIntoImgsSrc(
        ["offer-fg", "offer-preview-fg"],
        fgUpload.cachedFileArray[fgUpload.cachedFileArray.length - 1]
      );
    } else if (fg && !fgWasRemoved) {
      loadPathIntoImgsSrc(["offer-fg", "offer-preview-fg"], fg);
    } else {
      // Clear fg images when no file or explicitly removed
      const fgImg = document.getElementById("offer-fg") as HTMLImageElement;
      const fgPreviewImg = document.getElementById("offer-preview-fg") as HTMLImageElement;
      if (fgImg) fgImg.src = "";
      if (fgPreviewImg) fgPreviewImg.src = "";
    }

    if (bgUpload.cachedFileArray.length > 0) {
      loadFileIntoImgsSrc(
        ["offer-bg", "offer-preview-bg"],
        bgUpload.cachedFileArray[bgUpload.cachedFileArray.length - 1]
      );
    } else if (bg && !bgWasRemoved) {
      loadPathIntoImgsSrc(["offer-bg", "offer-preview-bg"], bg);
    } else {
      // Clear bg images when no file or explicitly removed
      const bgImg = document.getElementById("offer-bg") as HTMLImageElement;
      const bgPreviewImg = document.getElementById("offer-preview-bg") as HTMLImageElement;
      if (bgImg) bgImg.src = "";
      if (bgPreviewImg) bgPreviewImg.src = "";
    }
  };

  const closeWithAnimation = useCallback(() => {
    if (hasChanged) {
      const confirmLeave = window.confirm('У вас є незбережені зміни. Ви впевнені, що хочете вийти? Всі зміни буде втрачено.');
      if (!confirmLeave) {
        return;
      }
    }
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 200);
  }, [hasChanged, onClose]);

  const handleDelete = async () => {
    if (!params.offer?.id) return;
    const confirmed = window.confirm(`Видалити офер "${params.offer.caption || params.offer.name}"? Його буде видалено з усіх списків.`);
    if (!confirmed) return;
    setIsDeleting(true);
    setSaveError(null);
    try {
      await PanelService.deleteOffer(params.offer.id);
      onSave?.();
      setIsClosing(true);
      setTimeout(() => onClose(), 200);
    } catch (e: any) {
      setSaveError('Помилка видалення: ' + (e?.response?.data?.error || e.message || 'невідома'));
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    if (!id) {
      let maxId = 0;
      for (let offer of offers) {
        if (offer.id > maxId) maxId = offer.id;
      }
      setId(maxId + 1);
    }

    updateOfferImages();

    window.addEventListener(Events.IMAGE_ADDED, (e: Event) => {
      const { detail } = e as unknown as ImageAddedEvent;
      updateOfferImages();
      update();
      setHasChanged(true);
    });

    while (bgUpload.cachedFileArray.length > 0) {
      bgUpload.deleteFileAtIndex(0);
    }

    while (fgUpload.cachedFileArray.length > 0) {
      fgUpload.deleteFileAtIndex(0);
    }

    // Handle ESC key
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeWithAnimation();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  let onConfirm = async () => {
    setIsSaving(true);
    setSaveError(null);
    
    try {
      // Build the offer object with current values
      const selectedPartner = partnerIndex !== null ? partners[partnerIndex] : null;
      
      const keyTrimmed = generatedKey.trim();
      const graphic = { ...(offer.graphic || { logo: '', bg: '' }) };
      if (bgRemoved && bgUpload.cachedFileArray.length === 0) graphic.bg = '';
      if (fgRemoved && fgUpload.cachedFileArray.length === 0) graphic.logo = '';

      const offerToSave: Partial<TOffer> = {
        ...offer,
        id: id || 0,
        geo: geo || '',
        link: link || offer.link || '',
        partnerId: selectedPartner?.partnerId || offer.partnerId,
        graphic,
        offerwallType,
        texts: isMfoLike ? texts : [],
        emoji: '',
        noBackground: isMfoLike ? noBackground : false,
        generatedKey: keyTrimmed || undefined,
      };
      
      // Get files if any were uploaded
      const bgFile = bgUpload.cachedFileArray.length > 0 
        ? bgUpload.cachedFileArray[bgUpload.cachedFileArray.length - 1] 
        : undefined;
      const logoFile = fgUpload.cachedFileArray.length > 0 
        ? fgUpload.cachedFileArray[fgUpload.cachedFileArray.length - 1] 
        : undefined;
      
      await PanelService.upsertOffer(offerToSave, bgFile, logoFile);
      if (onSave) onSave();
      setIsClosing(true);
      setTimeout(() => {
        onClose();
      }, 200);
    } catch (error: any) {
      logger.error('Error saving offer', error);
      setSaveError(error?.response?.data?.message || error?.message || 'Помилка збереження');
    } finally {
      setIsSaving(false);
    }
  };

  let hasFgImage = () => {
    return fgUpload.cachedFileArray.length > 0 || (fg && !fgRemoved);
  };

  let hasBgImage = () => {
    return bgUpload.cachedFileArray.length > 0 || (bg && !bgRemoved);
  };

  let onFgUploadClick = () => {
    // Always open file picker - image will be replaced only if new one is uploaded
    setFgRemoved(false);
    fgUpload.emulateInputSelection();
  };

  let onBgUploadClick = () => {
    // Always open file picker - image will be replaced only if new one is uploaded
    setBgRemoved(false);
    bgUpload.emulateInputSelection();
  };

  let idElement = (
    <InputNumber className="offer-crud-input" id="id" disabled defaultValue={id || ""} />
  );

  let keyElement = (
    <Input
      className="offer-crud-input"
      id="generatedKey"
      value={generatedKey}
      onChange={(value) => {
        setGeneratedKey(value);
        setHasChanged(true);
      }}
      placeholder="Порожньо — згенерується автоматично (з транслитом)"
    />
  );

  let captionElement = (
    <Input 
      className="offer-crud-input" 
      id="caption" 
      value={offer?.caption || ''}
      onChange={(value) => {
        setOffer({ ...offer, caption: value });
        setHasChanged(true);
      }}
    />
  );
  
  let partnerElement = (
    <SelectPicker
      className="offer-crud-input"
      cleanable={false}
      style={{ width: "100%" }}
      value={partnerIndex}
      onChange={(value) => {
        setPartnerIndex(value);
        setHasChanged(true);
      }}
      data={partnersMapped}
      searchable
      locale={{ searchPlaceholder: 'Пошук' }}
    />
  );
  
  let geoElement = (
    <SelectPicker
      className="offer-crud-input"
      searchBy={(keyword: string, label: ReactNode, item: ItemDataType) => {
        const searchLabel = (item as any).searchLabel || '';
        const countryCode = item.value?.toString() || '';
        return (
          countryCode.toLowerCase() === keyword.toLowerCase() ||
          searchLabel.toLowerCase().includes(keyword.toLowerCase())
        );
      }}
      cleanable={false}
      value={geo}
      onChange={(value) => {
        setGeo(value);
        setHasChanged(true);
      }}
      data={countryListMapped}
      style={{ width: "100%" }}
      renderMenuItem={(label, item) => item.label}
      renderValue={(value) => {
        const countryData = countryList.getData().find(c => c.code === value);
        if (!countryData) return value;
        return (
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '18px' }}>{getCountryFlag(value as string)}</span>
            <span>{countryData.name}</span>
            <span style={{ fontWeight: 700, color: '#888' }}>{value}</span>
          </span>
        );
      }}
      locale={{ searchPlaceholder: 'Пошук' }}
    />
  );
  
  let linkElement = (
    <Input 
      as="textarea"
      className="offer-crud-input offer-crud-link-input" 
      id="link" 
      rows={1}
      value={offer?.link || link || ''}
      onChange={(value) => {
        setLink(value);
        setOffer({ ...offer, link: value });
        setHasChanged(true);
      }}
      style={{ 
        resize: 'none',
        overflow: 'hidden',
        minHeight: '36px'
      }}
      onInput={(e: any) => {
        const target = e.target;
        target.style.height = 'auto';
        target.style.height = target.scrollHeight + 'px';
      }}
    />
  );

  return (
    <div 
      className={`offer-crud-backdrop ${isClosing ? 'closing' : ''}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          closeWithAnimation();
        }
      }}
    >
      <div className="offer-crud-container">
        <div className="offer-crud-header">
          <div className="offer-crud-header-content">
            <h2 className="offer-crud-title">
              {params.offer ? (
                <>
                  {params.offer.caption || params.offer.name} <strong>{params.offer.geo}</strong>
                  {' '}[{partners.find(p => p.partnerId === params.offer?.partnerId)?.name || 'Unknown'}]
                </>
              ) : (
                `Створення оферу`
              )}
            </h2>
            <div className="offer-crud-actions">
              {params.offer && (
                <span className="offer-crud-id">#{params.offer.id}</span>
              )}
              {params.offer && (
                <Button
                  className="offer-crud-btn delete"
                  onClick={handleDelete}
                  appearance="subtle"
                  loading={isDeleting}
                  disabled={isSaving || isDeleting}
                >
                  Видалити
                </Button>
              )}
              <Button 
                className="offer-crud-btn cancel"
                onClick={closeWithAnimation} 
                appearance="subtle" 
                disabled={isSaving}
              >
                <CloseIcon /> Скасувати
              </Button>
              <Button 
                className="offer-crud-btn save"
                onClick={onConfirm} 
                appearance="primary"
                loading={isSaving}
                disabled={isSaving}
              >
                {isSaving ? 'Збереження...' : (params.offer ? "Зберегти" : "Створити")}
              </Button>
            </div>
          </div>
        </div>
        
        <div className="offer-crud-content">
          {saveError && (
            <div className="offer-crud-error">
              {saveError}
            </div>
          )}
          
          <div className="offer-crud-body">
            <div className="images-section">
              <div className="images-pickers-row">
                <div className="image-picker-container">
                  <span className="image-picker-label">{isMfoLike ? 'Зображення' : 'Фон'}</span>
                  <div className={`bg ${isMfoLike ? 'bg--mfo' : ''}`} onClick={onBgUploadClick}>
                    <img id="offer-bg" className="offer-bg" />
                    <div className="offer-bg-overlay"></div>
                    {hasBgImage() ? (
                      <>
                        <span className="bg-remove">
                          <Reload />
                        </span>
                        <span className="image-delete-btn" onClick={(e) => {
                          e.stopPropagation();
                          while (bgUpload.cachedFileArray.length > 0) bgUpload.deleteFileAtIndex(0);
                          setBgRemoved(true);
                          updateOfferImages(fgRemoved, true);
                          setHasChanged(true);
                        }}>
                          <CloseIcon />
                        </span>
                      </>
                    ) : (
                      <span className="bg-upload">
                        <FileUpload />
                      </span>
                    )}
                  </div>
                </div>
                {!isMfoLike && (
                  <>
                    <span className="plus-spacer noselect">+</span>
                    <div className="image-picker-container">
                      <span className="image-picker-label">Лого</span>
                      <div className="fg" onClick={onFgUploadClick}>
                        <div className="offer-fg-overlay"></div>
                        <img id="offer-fg" className="offer-fg" />
                        {hasFgImage() ? (
                          <>
                            <span className="fg-remove">
                              <Reload />
                            </span>
                            <span className="image-delete-btn" onClick={(e) => {
                              e.stopPropagation();
                              while (fgUpload.cachedFileArray.length > 0) fgUpload.deleteFileAtIndex(0);
                              setFgRemoved(true);
                              updateOfferImages(true, bgRemoved);
                              setHasChanged(true);
                            }}>
                              <CloseIcon />
                            </span>
                          </>
                        ) : (
                          <span className="fg-upload">
                            <FileUpload />
                          </span>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
              <span className="arrow-down noselect">⬇</span>
            
            {!isMfoLike ? (
              <div className="offer-preview refined-shadow">
                <img
                  className="offer-background"
                  crossOrigin="anonymous"
                  id="offer-preview-bg"
                ></img>

                <a className="item-link">
                  <div className="offer-image-container">
                    <img id="offer-preview-fg" className="offer-foreground"></img>
                  </div>
                  <span className="refined-shadow offer-play-button noselect">
                    ГРАТИ
                  </span>
                </a>
              </div>
            ) : (
              <div className="mfo-preview-card">
                <div className="mfo-preview-top">
                  <div className="mfo-preview-logo-wrap">
                    <img id="offer-preview-bg" className="mfo-preview-bg" crossOrigin="anonymous" />
                  </div>
                  <div className="mfo-preview-info">
                    <div className="mfo-preview-name">
                      {offer.caption || 'Назва оферу'}
                      {texts[7] === '1' && <span className="mfo-preview-star">⭐</span>}
                    </div>
                    <div className="mfo-preview-badges">
                      {texts[4] && (
                        <span className="mfo-preview-badge mfo-preview-badge-geo">{texts[4]}</span>
                      )}
                      {texts[0] && (
                        <span className="mfo-preview-badge mfo-preview-badge-green">{texts[0]}</span>
                      )}
                      {texts[1] && (
                        <span className="mfo-preview-badge mfo-preview-badge-blue">{texts[1]}</span>
                      )}
                      {texts[6] && (
                        <span className="mfo-preview-badge mfo-preview-badge-pink">{texts[6]}</span>
                      )}
                    </div>
                  </div>
                </div>
                {(texts[3] || texts[2]) && (
                  <div className="mfo-preview-bottom">
                    {texts[3] && (
                      <div className="mfo-preview-label3">{texts[3]}</div>
                    )}
                    {texts[2] && (
                      <div className={`mfo-preview-cta ${texts[5] === 'cup' ? 'cta-gold' : ''} ${texts[5] === 'diamond' ? 'cta-blue' : ''} ${texts[5] === 'heart' ? 'cta-pink' : ''} ${texts[5] === 'none_green' || !texts[5] || texts[5] === 'arrow' ? 'cta-green' : ''}`}>
                        <span>{texts[2]}</span>
                        {texts[5] !== 'none' && texts[5] !== 'none_green' && texts[5] !== 'diamond' && (
                          <span className={`mfo-preview-cta-icon ${texts[5] === 'cup' ? 'icon-gold' : ''} ${texts[5] === 'heart' ? 'icon-pink' : ''} ${texts[5] === 'arrow' || !texts[5] ? 'icon-green' : ''}`}>
                            {texts[5] === 'cup' ? '🏆' : texts[5] === 'heart' ? '💕' : '↗'}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            </div>
            <Stack
              className="offer-crud-form"
              spacing={7}
              direction="column"
              alignItems="stretch"
            >
              <Form.ControlLabel>Назва</Form.ControlLabel>
              {captionElement}
              <Form.ControlLabel>Гео</Form.ControlLabel>
              {geoElement}
              <Form.ControlLabel>Ключ</Form.ControlLabel>
              {keyElement}
              <Form.ControlLabel>Партнерка</Form.ControlLabel>
              {partnerElement}
              <Form.ControlLabel>Посилання</Form.ControlLabel>
              {linkElement}
              
            </Stack>
            
            {isMfoLike && (
              <div className="mfo-fields-column">
                <Form.ControlLabel className="mfo-column-title">MFO параметри</Form.ControlLabel>
                
                <Form.ControlLabel>Гео бейдж (пусто = приховати)</Form.ControlLabel>
                <Input
                  className="offer-crud-input"
                  value={texts[4] || ''}
                  placeholder="напр. 🇺🇦 Україна"
                  onChange={(value) => {
                    const newTexts = [...texts];
                    newTexts[4] = value;
                    setTexts(newTexts);
                    setHasChanged(true);
                  }}
                />
                
                <Form.ControlLabel>Значок 1 (зелений)</Form.ControlLabel>
                <Input
                  className="offer-crud-input"
                  value={texts[0] || ''}
                  placeholder="напр. 🌿 Мікрофінанси"
                  onChange={(value) => {
                    const newTexts = [...texts];
                    newTexts[0] = value;
                    setTexts(newTexts);
                    setHasChanged(true);
                  }}
                />
                <Form.ControlLabel>Значок 2 (синій)</Form.ControlLabel>
                <Input
                  className="offer-crud-input"
                  value={texts[1] || ''}
                  placeholder="напр. 💰 Швидко"
                  onChange={(value) => {
                    const newTexts = [...texts];
                    newTexts[1] = value;
                    setTexts(newTexts);
                    setHasChanged(true);
                  }}
                />
                <Form.ControlLabel>Значок 3 (рожевий)</Form.ControlLabel>
                <Input
                  className="offer-crud-input"
                  value={texts[6] || ''}
                  placeholder="напр. 💎 Преміум"
                  onChange={(value) => {
                    const newTexts = [...texts];
                    newTexts[6] = value;
                    setTexts(newTexts);
                    setHasChanged(true);
                  }}
                />
                <Form.ControlLabel>Стрілка (текст)</Form.ControlLabel>
                <Input
                  className="offer-crud-input"
                  value={texts[2] || ''}
                  placeholder="напр. ПОДАТИ ЗАЯВКУ"
                  onChange={(value) => {
                    const newTexts = [...texts];
                    newTexts[2] = value;
                    setTexts(newTexts);
                    setHasChanged(true);
                  }}
                />
                <Form.ControlLabel>Стиль стрілки</Form.ControlLabel>
                <div className="arrow-style-selector">
                  <label className={`arrow-option ${(!texts[5] || texts[5] === 'arrow') ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="arrowStyle"
                      value="arrow"
                      checked={!texts[5] || texts[5] === 'arrow'}
                      onChange={() => {
                        const newTexts = [...texts];
                        newTexts[5] = 'arrow';
                        setTexts(newTexts);
                        setHasChanged(true);
                      }}
                    />
                    <span className="arrow-icon arrow-green">↗</span>
                  </label>
                  <label className={`arrow-option ${texts[5] === 'cup' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="arrowStyle"
                      value="cup"
                      checked={texts[5] === 'cup'}
                      onChange={() => {
                        const newTexts = [...texts];
                        newTexts[5] = 'cup';
                        setTexts(newTexts);
                        setHasChanged(true);
                      }}
                    />
                    <span className="arrow-icon arrow-gold">🏆</span>
                  </label>
                  <label className={`arrow-option ${texts[5] === 'heart' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="arrowStyle"
                      value="heart"
                      checked={texts[5] === 'heart'}
                      onChange={() => {
                        const newTexts = [...texts];
                        newTexts[5] = 'heart';
                        setTexts(newTexts);
                        setHasChanged(true);
                      }}
                    />
                    <span className="arrow-icon arrow-pink">💕</span>
                  </label>
                  <label className={`arrow-option ${texts[5] === 'none_green' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="arrowStyle"
                      value="none_green"
                      checked={texts[5] === 'none_green'}
                      onChange={() => {
                        const newTexts = [...texts];
                        newTexts[5] = 'none_green';
                        setTexts(newTexts);
                        setHasChanged(true);
                      }}
                    />
                    <span className="arrow-label arrow-green">Без</span>
                  </label>
                  <label className={`arrow-option ${texts[5] === 'none' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="arrowStyle"
                      value="none"
                      checked={texts[5] === 'none'}
                      onChange={() => {
                        const newTexts = [...texts];
                        newTexts[5] = 'none';
                        setTexts(newTexts);
                        setHasChanged(true);
                      }}
                    />
                    <span className="arrow-label">Без</span>
                  </label>
                  <label className={`arrow-option ${texts[5] === 'diamond' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="arrowStyle"
                      value="diamond"
                      checked={texts[5] === 'diamond'}
                      onChange={() => {
                        const newTexts = [...texts];
                        newTexts[5] = 'diamond';
                        setTexts(newTexts);
                        setHasChanged(true);
                      }}
                    />
                    <span className="arrow-label arrow-blue">Без</span>
                  </label>
                </div>
                <Form.ControlLabel>Нижній напис (сірий)</Form.ControlLabel>
                <Input
                  className="offer-crud-input"
                  value={texts[3] || ''}
                  placeholder="напр. до 50 000 грн"
                  onChange={(value) => {
                    const newTexts = [...texts];
                    newTexts[3] = value;
                    setTexts(newTexts);
                    setHasChanged(true);
                  }}
                />
                <label className="mfo-checkbox-label">
                  <input
                    type="checkbox"
                    checked={noBackground}
                    onChange={(e) => {
                      setNoBackground(e.target.checked);
                      setHasChanged(true);
                    }}
                  />
                  <span>Без фону</span>
                </label>
                <label className="mfo-checkbox-label">
                  <input
                    type="checkbox"
                    checked={texts[7] === '1'}
                    onChange={(e) => {
                      const newTexts = [...texts];
                      newTexts[7] = e.target.checked ? '1' : '';
                      setTexts(newTexts);
                      setHasChanged(true);
                    }}
                  />
                  <span>⭐ Зірка біля назви</span>
                </label>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfferCrudModal;
